export type GuideFaq = {
  question: string;
  answer: string;
};

export type GuideSource = {
  label: string;
  url: string;
};

export type GuideSection = {
  heading: string;
  body: string[];
};

export type GuideArticle = {
  slug: string;
  title: string;
  description: string;
  category: string;
  author: string;
  reviewer: string;
  publishedDate: string;
  updatedDate: string;
  readingTime: string;
  heroSummary: string;
  introduction: string[];
  keyTakeaways: string[];
  sections: GuideSection[];
  commonMistakes: string[];
  faq: GuideFaq[];
  sources: GuideSource[];
  relatedGuides: Array<{ label: string; href: string }>;
};

const guideIndex = [
  {
    slug: "california-speeding-ticket-cost",
    title: "How Much Is a Speeding Ticket in California?",
    category: "Ticket Costs",
    description: "Understand base fines, assessments, DMV points and why the court total may be higher than the base amount.",
  },
  {
    slug: "how-to-fight-a-speeding-ticket-in-california",
    title: "How to Fight a Speeding Ticket in California",
    category: "Speeding Tickets",
    description: "Learn practical response options, evidence questions and common mistakes before deciding how to handle a California speeding ticket.",
  },
  {
    slug: "trial-by-written-declaration-california",
    title: "Trial by Written Declaration California",
    category: "Court Procedures",
    description: "Understand California's written-declaration process, TR-205, deadlines and evidence preparation basics.",
  },
  {
    slug: "how-to-look-up-california-traffic-ticket",
    title: "How to Look Up Your California Traffic Ticket / Citation Number",
    category: "Citation Lookup",
    description: "Learn how to find a California traffic ticket or citation number before deciding how to respond.",
  },
  {
    slug: "cvc-21461-a-violation-california",
    title: "CVC 21461(a) Violation Explained",
    category: "Violation Codes",
    description: "Understand what a California Vehicle Code 21461(a) citation may mean and what details to verify.",
  },
  {
    slug: "california-traffic-ticket-points",
    title: "California Traffic Ticket Points",
    category: "DMV Points and Traffic School",
    description: "Learn how California DMV points can relate to traffic convictions, traffic school and license-risk decisions.",
  },
  {
    slug: "speeding-over-100-mph-california",
    title: "Speeding Over 100 MPH California",
    category: "Speeding Tickets",
    description: "Understand why California speeding-over-100 cases require careful review of the citation, speed evidence and court options.",
  },
  {
    slug: "radar-and-lidar-evidence-california",
    title: "Radar and LiDAR Evidence California",
    category: "Evidence and Records",
    description: "Learn what radar, LiDAR and speed-measurement evidence can mean in a California speeding-ticket review.",
  },
] as const;

export const implementedGuideSlugs = guideIndex.map((guide) => guide.slug);

const guideHref = (slug: string) => `/guides/${slug}/`;

const relatedGuidesFor = (currentSlug: string) =>
  guideIndex
    .filter((guide) => guide.slug !== currentSlug)
    .map((guide) => ({ label: guide.title, href: guideHref(guide.slug) }));

const officialSources = {
  courtsTraffic: {
    label: "California Courts - Traffic tickets",
    url: "https://selfhelp.courts.ca.gov/traffic",
  },
  trialDeclaration: {
    label: "California Courts - Trial by written declaration",
    url: "https://selfhelp.courts.ca.gov/traffic/trial-declaration",
  },
  tr205: {
    label: "California Courts form TR-205 - Request for Trial by Written Declaration",
    url: "https://selfhelp.courts.ca.gov/jcc-form/TR-205",
  },
  tr200: {
    label: "California Courts form TR-200 - Instructions to Defendant",
    url: "https://selfhelp.courts.ca.gov/jcc-form/TR-200",
  },
  tr210: {
    label: "California Courts form TR-210 - Notice and Instructions to Arresting Officer",
    url: "https://selfhelp.courts.ca.gov/jcc-form/TR-210",
  },
  tr235: {
    label: "California Courts form TR-235 - Officer's Declaration",
    url: "https://selfhelp.courts.ca.gov/jcc-form/TR-235",
  },
  trafficSchool: {
    label: "California Courts - Traffic school",
    url: "https://selfhelp.courts.ca.gov/traffic/traffic-school",
  },
  dmvViolations: {
    label: "California DMV - Traffic violations",
    url: "https://www.dmv.ca.gov/portal/driver-education-and-safety/dmv-safety-guidelines-actions/traffic-violations/",
  },
  cvc22348: {
    label: "California Vehicle Code section 22348",
    url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=VEH&sectionNum=22348.",
  },
  cvc22349: {
    label: "California Vehicle Code section 22349",
    url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=VEH&sectionNum=22349.",
  },
  cvc22350: {
    label: "California Vehicle Code section 22350",
    url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=VEH&sectionNum=22350.",
  },
  cvc40802: {
    label: "California Vehicle Code section 40802",
    url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=VEH&sectionNum=40802.",
  },
  cvc21461: {
    label: "California Vehicle Code section 21461",
    url: "https://leginfo.legislature.ca.gov/faces/codes_displaySection.xhtml?lawCode=VEH&sectionNum=21461.",
  },
  bailSchedule: {
    label: "California Courts - Uniform Bail and Penalty Schedule 2026",
    url: "https://www.lassen.courts.ca.gov/system/files/forms-and-filings/uniform-bail-and-penalty-schedule-effective-january-1-2026.pdf",
  },
};

const article = (articleData: Omit<GuideArticle, "author" | "reviewer" | "publishedDate" | "updatedDate" | "relatedGuides">): GuideArticle => ({
  author: "JusticeDrive Editorial Team",
  reviewer: "JusticeDrive Compliance Team",
  publishedDate: articleData.slug === "how-to-look-up-california-traffic-ticket" || articleData.slug === "cvc-21461-a-violation-california" ? "2026-08-01" : "2026-07-22",
  updatedDate: articleData.slug === "how-to-look-up-california-traffic-ticket" || articleData.slug === "cvc-21461-a-violation-california" ? "2026-08-01" : "2026-07-22",
  relatedGuides: relatedGuidesFor(articleData.slug),
  ...articleData,
});

export const guideArticles: Record<string, GuideArticle> = {
  "california-speeding-ticket-cost": article({
    slug: "california-speeding-ticket-cost",
    title: "How Much Is a Speeding Ticket in California?",
    description:
      "Learn how California speeding-ticket costs can include base fines, assessments, DMV consequences and court-specific amounts.",
    category: "Ticket Costs",
    readingTime: "7 min read",
    heroSummary:
      "California speeding-ticket cost is not just the base fine. The final court amount can include added assessments, local fees and longer-term consequences that should be reviewed before you decide what to do next.",
    introduction: [
      "A California speeding ticket can be confusing because the number drivers hear first is often not the number that appears on the final court notice.",
      "This guide explains the difference between base fine, court total, DMV point risk and traffic-school considerations without treating any estimate as an exact quote.",
    ],
    keyTakeaways: [
      "The court total can be higher than the base amount because assessments and fees may apply.",
      "County, court, violation code and case-specific facts can affect the amount shown by the court.",
      "A conviction may add a DMV point depending on the violation and eligibility factors.",
      "Cost is only one part of the decision; citation details and evidence gaps may also matter.",
    ],
    sections: [
      {
        heading: "Why the total is higher than the base fine",
        body: [
          "California traffic tickets often start with a base amount, but the total shown on a court notice can include additional assessments and fees. That is why the amount due may be much higher than the number drivers expect from a base schedule.",
          "The exact amount should be confirmed through the court notice, court portal or official bail schedule. JusticeDrive does not treat calculator output as a court quote.",
        ],
      },
      {
        heading: "County and court differences",
        body: [
          "The same violation can produce different practical totals because courts may apply local fees, administrative costs and case-specific adjustments.",
          "If your citation does not clearly show the violation code or court information, the court docket or charge entry may be needed before relying on any estimate.",
        ],
      },
      {
        heading: "DMV points and traffic school",
        body: [
          "A speeding conviction may add a DMV point depending on the violation. Traffic school may be available for some eligible one-point violations, but eligibility is not guaranteed.",
          "Drivers should verify court instructions, DMV rules and any notice from the court before choosing a response.",
        ],
      },
      {
        heading: "When it may be worth reviewing the citation",
        body: [
          "The amount due is only one part of the decision. It may also matter whether the citation clearly lists the charge, how the alleged speed was measured, whether the location is clear and what records support the officer's statement.",
          "JusticeDrive can review the citation details and identify possible evidence gaps before you decide whether to pay, request records or prepare a response.",
        ],
      },
    ],
    commonMistakes: [
      "Assuming the base fine is the same as the amount due on the court notice.",
      "Ignoring DMV point consequences when comparing payment, traffic school and contesting options.",
      "Relying on a cost estimate before confirming the exact violation code.",
      "Paying before reviewing whether the citation details are complete and accurate.",
    ],
    faq: [
      {
        question: "Is the calculator an exact court quote?",
        answer:
          "No. A calculator can only provide an informational estimate when verified data is available. The actual amount is determined by the court and may vary by county, violation code and case-specific assessments.",
      },
      {
        question: "Will a speeding ticket add a DMV point?",
        answer:
          "A conviction may add a DMV point depending on the violation. Drivers should review the court notice and DMV materials for their specific situation.",
      },
      {
        question: "Should I pay immediately?",
        answer:
          "Payment may resolve the ticket but can also have consequences depending on the citation and court process. Review the citation and court notice before deciding what to do next.",
      },
    ],
    sources: [officialSources.courtsTraffic, officialSources.bailSchedule, officialSources.dmvViolations],
  }),
  "how-to-fight-a-speeding-ticket-in-california": article({
    slug: "how-to-fight-a-speeding-ticket-in-california",
    title: "How to Fight a Speeding Ticket in California",
    description:
      "Learn how California speeding tickets can be reviewed, what evidence may matter, and which response options drivers commonly compare.",
    category: "Speeding Tickets",
    readingTime: "7 min read",
    heroSummary:
      "Fighting a California speeding ticket starts with the citation itself: the code listed, the alleged speed, the location, the officer's statement and the records that support the charge.",
    introduction: [
      "The first decision after a California speeding ticket is not simply pay or fight. You need to know what the ticket alleges, which court is handling it, what deadline applies, and whether the record shows enough detail to choose a response.",
      "This guide walks through the practical review a driver can do before deciding whether to pay, ask about traffic school, appear in court, or prepare a Trial by Written Declaration. It is general information, not legal advice.",
    ],
    keyTakeaways: [
      "Start by confirming the exact violation code, court, deadline, speed and location shown on the citation.",
      "Possible review areas can include officer procedure, speed-measurement method, location, weather and missing court records.",
      "A Trial by Written Declaration may be an option for some California traffic infractions, but deadlines and court instructions matter.",
      "No guide can tell you whether you will win; the goal is to understand what should be checked before deciding.",
    ],
    sections: [
      {
        heading: "Step 1: Confirm what the citation actually says",
        body: [
          "Read the front and back of the citation carefully. Look for the violation code, alleged speed, posted speed, location, date, court name, officer information and any notes about the measurement method.",
          "If the code or description is missing, do not guess. The court docket, complete citation record or agency records may be needed to confirm the exact charge.",
          "The California Courts traffic self-help materials emphasize following the instructions from the court handling the ticket. That means the court notice, courtesy notice, online case portal, or clerk instructions can be just as important as the handwritten ticket.",
          "Make a clean list of confirmed facts and separate it from anything you only remember from the stop. For example, citation speed, posted speed, and code section are citation facts; what the officer said at the window is user context unless it appears in an official record.",
        ],
      },
      {
        heading: "Step 2: Compare your response options",
        body: [
          "California traffic matters can involve several different paths: paying the ticket, requesting traffic school if eligible, appearing in court, or contesting the matter by written declaration where available.",
          "Each option can have different timing, cost and DMV consequences. The right next step depends on your citation, court notice and goals.",
          "Paying usually means accepting the court's resolution path for the charge shown. Traffic school, if offered and completed under court instructions, can affect how an eligible conviction is handled for reporting purposes, but it is not the same as proving the ticket wrong.",
          "Contesting the ticket means asking the court to review the allegation. That can happen through an in-person process or, for some eligible matters, a written-declaration process. Before contesting, identify what you can actually dispute from the citation and records.",
        ],
      },
      {
        heading: "Step 3: Identify the alleged Vehicle Code section",
        body: [
          "For California speeding tickets, common code sections can include maximum-speed provisions such as Vehicle Code section 22349 or basic-speed allegations under section 22350. The exact section matters because it affects what the allegation is and what evidence may be relevant.",
          "Do not insert a code that is not on the ticket. If the citation only says \"speeding\" or gives a handwritten speed without a code, the safer next step is to confirm the court charge entry or complete citation record.",
          "A basic-speed allegation may involve conditions such as traffic, weather, visibility, roadway width, and other circumstances. A maximum-speed allegation may focus more directly on the posted or statutory maximum. The citation should guide which issue you review.",
        ],
      },
      {
        heading: "Step 4: Identify what evidence may matter",
        body: [
          "For speeding allegations, useful questions may include how the speed was measured, whether radar or LiDAR was used, whether the location is clear, what the weather and traffic conditions were, and whether required records are available.",
          "These questions do not prove a defense by themselves. They show what may need to be verified before relying on a response strategy.",
          "If the citation, officer notes, or agency record indicates radar or LiDAR, device records and officer training records may be relevant. If the record indicates pacing or visual estimate, a different set of facts may matter.",
          "Location can also affect review. A vague freeway, intersection, or exit description may need confirmation before relying on road-condition or survey arguments. Geocoding alone should not be treated as legal proof of the location or speed zone.",
        ],
      },
      {
        heading: "Step 5: Contesting in person",
        body: [
          "An in-person contest generally requires following the court's appearance instructions and being prepared to address the charge, the officer's evidence, and any documents you want the court to consider.",
          "A driver should bring or organize only evidence that is tied to the citation: photographs, diagrams, notices, records, or written notes that clarify the alleged violation. Unrelated screenshots or broad complaints can weaken clarity.",
          "The court, not JusticeDrive, controls deadlines, hearing procedures, continuance rules, and what documents are accepted. Always compare any preparation checklist against the court notice.",
        ],
      },
      {
        heading: "Step 6: Trial by Written Declaration",
        body: [
          "California Courts describe Trial by Written Declaration as a process that may allow eligible traffic matters to be contested in writing. It is not the same thing as sending an informal letter.",
          "The written-declaration process is covered in more detail in the dedicated Trial by Written Declaration guide. For this overview, the key point is that written arguments should be organized around citation facts, court instructions, and supporting exhibits.",
          "If you use this path, confirm the required forms, deadline, court address, submission method, and any bail or deposit instructions directly from the court handling the ticket.",
        ],
      },
      {
        heading: "Step 7: Prepare a focused statement",
        body: [
          "If you contest the ticket, a clear statement should stay tied to citation facts and available evidence. Avoid exaggerated claims, unsupported accusations or copied templates that do not match your situation.",
          "A stronger review separates what the citation proves, what you personally observed and what records still need confirmation.",
          "A useful structure is: identify the charge, state the facts shown on the citation, explain what fact or record is missing or inconsistent, then attach only exhibits that support that point.",
          "Avoid saying a missing record proves dismissal. More precise language is that the record is needed to verify the allegation, measurement basis, location, or officer procedure.",
        ],
      },
      {
        heading: "Step 8: Track deadlines and court instructions",
        body: [
          "Court deadlines and submission rules can matter as much as the argument itself. Always check the notice from the court and any instructions on the citation.",
          "If you are unsure about serious consequences, license risk or legal strategy, consider speaking with a qualified attorney.",
          "Keep copies of every document, screenshot confirmation, mail receipt, or court submission confirmation. If the court provides an online portal, save the case number and status screen after any action.",
          "For citations with possible license consequences, commercial-driver concerns, or high-speed allegations, individual legal advice may be appropriate before choosing a path.",
        ],
      },
      {
        heading: "What JusticeDrive can and cannot do",
        body: [
          "JusticeDrive can help read the citation, organize extracted facts, flag missing information, and prepare document-review workflows. It can also suggest records that may clarify the charge or evidence basis.",
          "JusticeDrive cannot appear for you, provide legal advice, guarantee traffic school eligibility, guarantee dismissal, or decide what you should do. The driver remains responsible for court deadlines, filing instructions, and final review of any document.",
          "Use the free citation review to understand what the ticket appears to say and what needs confirmation before you make a paid or court-facing decision.",
        ],
      },
    ],
    commonMistakes: [
      "Paying before checking whether the citation lists the correct violation and court information.",
      "Assuming radar or LiDAR was used when the citation does not say so.",
      "Copying a generic defense that does not match the facts on the citation.",
      "Missing court deadlines or failing to follow the court's submission instructions.",
      "Treating a missing record as proof of dismissal rather than a point that needs verification.",
    ],
    faq: [
      {
        question: "Can I fight a California speeding ticket without a lawyer?",
        answer:
          "Some drivers handle traffic infractions on their own, including through written-declaration procedures where available. Serious matters, license-risk situations or complex facts may require legal advice.",
      },
      {
        question: "What should I check first?",
        answer:
          "Start with the violation code, alleged speed, posted speed, location, court information, deadline, officer notes and whether the citation clearly explains the charge.",
      },
      {
        question: "Does a missing violation code help?",
        answer:
          "A missing or unclear code is not an automatic result. It may mean the court docket, complete citation record or agency records should be reviewed before relying on any strategy.",
      },
      {
        question: "Can JusticeDrive tell me whether I will win?",
        answer:
          "No. JusticeDrive provides software-assisted review and document workflow support. It does not provide legal advice or guarantee a court outcome.",
      },
    ],
    sources: [officialSources.courtsTraffic, officialSources.trialDeclaration, officialSources.cvc22349, officialSources.cvc22350],
  }),
  "trial-by-written-declaration-california": article({
    slug: "trial-by-written-declaration-california",
    title: "Trial by Written Declaration California",
    description:
      "Learn how California Trial by Written Declaration works, what TR-205 is, and what drivers should check before preparing a written response.",
    category: "Court Procedures",
    readingTime: "6 min read",
    heroSummary:
      "Trial by Written Declaration is a California traffic court process that may let eligible drivers contest certain citations in writing instead of appearing in person.",
    introduction: [
      "California's Trial by Written Declaration process gives some drivers a way to contest eligible traffic citations through written statements and evidence instead of a live court appearance.",
      "The process is procedural. The court handling the ticket controls deadlines, submission instructions, and what must be filed. This guide explains the forms and workflow at a practical level without giving legal advice.",
    ],
    keyTakeaways: [
      "California Courts describe Trial by Written Declaration as a way to contest some traffic infractions in writing.",
      "The TR-205 form is commonly used, but drivers must follow their court's current instructions.",
      "A useful declaration should match the citation facts and avoid unsupported claims.",
      "Deadlines, bail/fine deposit rules and evidence attachments should be verified with the court.",
    ],
    sections: [
      {
        heading: "What Trial by Written Declaration means",
        body: [
          "In a Trial by Written Declaration, the driver submits a written statement and any supporting materials for court review. The officer may also submit a written statement.",
          "The court then reviews the written materials and issues a decision. Availability and procedural details depend on the type of citation and court instructions.",
          "California Courts describe the process as a way to have the case decided without going to court in person for eligible traffic infractions. That does not mean every citation or every driver qualifies.",
          "The judge reviews what is submitted. The driver should therefore make the declaration readable, fact-specific, and organized around the charge shown on the citation.",
        ],
      },
      {
        heading: "What TR-205 is used for",
        body: [
          "TR-205 is the Judicial Council form commonly associated with California Trial by Written Declaration. It gives the driver a structured way to submit the request and statement.",
          "The form should be completed carefully. The statement should be factual, concise and consistent with the citation and supporting evidence.",
          "The TR-205 page is an official California Courts form page. It should be treated as the starting point for the driver's written declaration, but not as a substitute for the local court's own instructions.",
          "If a citation has missing or unclear facts, the statement should not invent them. It can instead explain that a particular record or charge detail needs confirmation.",
        ],
      },
      {
        heading: "How TR-200, TR-210, and TR-235 fit into the process",
        body: [
          "California Courts publish TR-200 as instructions for the defendant in a Trial by Written Declaration. That form is important because it explains the procedure and should be read before drafting the statement.",
          "TR-210 is the notice and instructions sent to the arresting or citing officer. It reflects that the officer may also participate in the written process rather than the driver being the only person submitting paperwork.",
          "TR-235 is the officer's declaration form. If the officer submits a declaration, it may describe the officer's observations, measurement method, location, and other facts supporting the citation.",
          "For a driver, these forms matter because they show the written-declaration process is a structured exchange of court materials, not a one-sided appeal letter.",
        ],
      },
      {
        heading: "What to prepare before writing",
        body: [
          "Review the citation, court notice, alleged violation, location, officer notes, measurement method and any records that may clarify the charge.",
          "If the citation is incomplete, it may be useful to confirm the court docket or complete citation record before relying on a specific argument.",
          "Build a short fact file before drafting: citation code, alleged speed, posted speed, exact location, weather, traffic conditions, officer comments, and any documents the court or agency has provided.",
          "If you plan to include exhibits, label them clearly and refer to them in the statement. A photograph, map, or record is easier for the court to understand when the declaration explains why it matters.",
        ],
      },
      {
        heading: "Evidence and attachments",
        body: [
          "Evidence might include clear photos, maps, court records, weather information or documents that directly support what you are saying. Do not attach unrelated material just to make the packet longer.",
          "For speed-related citations, device records or measurement-method documentation may be relevant only when the citation or record supports that type of issue.",
          "A driver might consider photographs of signage, roadway layout, visibility conditions, the citation reverse side, or official records that clarify the charge. The best exhibits are specific to the citation.",
          "If the issue is radar or LiDAR, the more precise question is whether the record shows a device was used and what records would verify that device or the officer's use of it.",
        ],
      },
      {
        heading: "Before submitting: practical checklist",
        body: [
          "Verify the court deadline from the court notice or portal, not from memory. Confirm the court address, online submission option if any, required forms, and whether the court requires bail or a deposit with the declaration.",
          "Complete required forms, organize the declaration, label exhibits, keep copies, and preserve proof of submission. If sending by mail, consider the court's instructions about acceptable mailing methods and timing.",
          "Review the final packet for consistency. The name, citation number, court, violation code, date, and location should match the citation or clearly explain why a detail is unknown.",
        ],
      },
      {
        heading: "After the decision",
        body: [
          "California Courts materials explain that additional options may exist after a written-declaration decision, but the details depend on court rules and timing.",
          "Read every court notice carefully and keep copies of what you submit.",
          "If the court sends a decision, read it fully before taking the next step. Some drivers may have additional procedural options, but those options depend on timing and the court's instructions.",
          "Do not assume that silence means the case is finished. Track the case status until the court record clearly shows what happened.",
        ],
      },
    ],
    commonMistakes: [
      "Submitting a generic template that does not match the citation facts.",
      "Missing the court deadline or sending documents to the wrong court address.",
      "Forgetting to verify whether the matter is eligible for written declaration.",
      "Claiming radar or LiDAR problems without a device clue or supporting record.",
      "Failing to keep a copy of the submitted materials.",
    ],
    faq: [
      {
        question: "Is Trial by Written Declaration available for every ticket?",
        answer:
          "No. Availability depends on the type of citation and court rules. Drivers should confirm eligibility through the court notice or California Courts resources.",
      },
      {
        question: "Do I have to appear in court?",
        answer:
          "The written-declaration process may allow eligible matters to be reviewed in writing, but court instructions and deadlines must be followed.",
      },
      {
        question: "What should the statement include?",
        answer:
          "A statement should explain relevant facts clearly and connect them to the citation and available evidence. It should not promise a result or rely on unsupported claims.",
      },
      {
        question: "Can JusticeDrive prepare the form for me?",
        answer:
          "JusticeDrive can help generate a court-ready draft for user review in supported workflows, but it is not a law firm and does not provide legal advice.",
      },
    ],
    sources: [
      officialSources.trialDeclaration,
      officialSources.tr205,
      officialSources.tr200,
      officialSources.tr210,
      officialSources.tr235,
      officialSources.courtsTraffic,
    ],
  }),
  "how-to-look-up-california-traffic-ticket": article({
    slug: "how-to-look-up-california-traffic-ticket",
    title: "How to Look Up Your California Traffic Ticket / Citation Number",
    description:
      "Learn practical ways to find a California traffic ticket, citation number or case status before deciding whether to pay, request records or prepare a response.",
    category: "Citation Lookup",
    readingTime: "5 min read",
    heroSummary:
      "If you lost the paper ticket or cannot read the citation number, start by locating the official court record before choosing how to respond.",
    introduction: [
      "A missing citation number can stall every next step. You may need it to check the court portal, confirm the deadline, request records, prepare a written declaration or recover the full charge details.",
      "This guide explains where the number may appear and how to approach a California traffic-ticket lookup without relying on unofficial guesses or county links that may not match your case.",
    ],
    keyTakeaways: [
      "The citation number may appear on the ticket, court notice, reminder notice or court record.",
      "Traffic tickets are handled by the county court connected to the citation, so lookup steps can vary by county.",
      "If the citation number is missing, the court notice, driver information or vehicle information may help the court identify the matter.",
      "Once you find the citation, review the code, location, court instructions and deadline before choosing a response.",
    ],
    sections: [
      {
        heading: "Where the citation number may be located",
        body: [
          "Start with the original citation. The citation number may be printed near the top, near the court information, or in a box labeled citation, ticket, notice to appear or case reference.",
          "If the paper copy is missing, check any reminder notice, courtesy notice, court email, mailed court document or agency notice connected to the ticket. The court record may use a citation number, case number or another court-specific reference.",
          "Do not assume that every number on the ticket is the citation number. License plate numbers, officer ID numbers, agency numbers and form numbers can appear on the same page.",
        ],
      },
      {
        heading: "How California traffic ticket lookup differs by county",
        body: [
          "California traffic tickets are handled through the county court connected to the citation. That means lookup pages, search fields and terminology can vary from one county to another.",
          "Some courts may let drivers search by citation number. Others may offer lookup through a driver's license, name, date of birth, vehicle information or a court case number. The safest starting point is the official court named on the citation or the California Courts traffic self-help resources.",
          "JusticeDrive does not publish invented county portal links. If a specific county link is not confirmed through official sources, use the court named on the citation or California Courts resources to find the correct court.",
        ],
      },
      {
        heading: "What to do if you lost the ticket",
        body: [
          "If the physical citation is lost, gather the information that can help the court identify the case: name, date of birth, driver's license information, vehicle plate, approximate citation date, city or county, and issuing agency if known.",
          "Contact or search the court connected to the place where the citation was issued. California Courts materials direct drivers to the court or agency listed on the citation for ticket-specific information.",
          "If you are not sure which court has the case, start with official California Courts traffic resources rather than a third-party lookup page.",
        ],
      },
      {
        heading: "What to check once you find the citation",
        body: [
          "After locating the ticket, confirm the violation code, court name, deadline, location, alleged facts, officer or agency information, and whether the record matches the paper citation.",
          "If the court portal shows a different code or description than the citation image, preserve both records and resolve the mismatch before relying on any response strategy.",
          "A lookup result is the beginning of the review, not the end. It tells you what case the court has, but the full citation and supporting records may still need confirmation.",
        ],
      },
      {
        heading: "Using the citation in a free JusticeDrive review",
        body: [
          "Once you have the citation or a clear court record, you can upload it to JusticeDrive for a free initial review. The review can help organize citation details, missing fields, evidence gaps and possible next steps.",
          "If you only have partial information, the review may identify what still needs confirmation, such as a missing violation code, unclear location or incomplete court information.",
          "JusticeDrive is not a law firm and does not provide legal advice. It helps structure the citation review so you can understand what records or court instructions may need attention.",
        ],
      },
    ],
    commonMistakes: [
      "Using an unofficial search result instead of the court connected to the citation.",
      "Mistaking an officer ID, agency number or vehicle plate for the citation number.",
      "Waiting for a reminder notice without checking the court deadline shown on the citation or court record.",
      "Assuming every county uses the same lookup portal or search fields.",
      "Preparing a response before confirming the exact code, court and deadline.",
    ],
    faq: [
      {
        question: "How do I find my California traffic citation number?",
        answer:
          "Start with the ticket, court notice, reminder notice or official court record. The citation number may also be called a ticket number, notice number or case reference depending on the court.",
      },
      {
        question: "What if I lost my California traffic ticket?",
        answer:
          "Use the court named on the citation if you know it, or start with official California Courts traffic resources. Gather identifying details such as name, license, vehicle plate, approximate date, county and issuing agency.",
      },
      {
        question: "Can I look up a ticket without the citation number?",
        answer:
          "Some courts may offer other search options, but search fields vary by county. If the number is missing, contact or search the official court connected to the citation.",
      },
      {
        question: "What should I do after I find the ticket?",
        answer:
          "Review the violation code, court instructions, deadline, location and any missing details before deciding whether to pay, request records, seek traffic school or prepare a response.",
      },
    ],
    sources: [officialSources.courtsTraffic, officialSources.dmvViolations],
  }),
  "cvc-21461-a-violation-california": article({
    slug: "cvc-21461-a-violation-california",
    title: "CVC 21461(a) Violation Explained",
    description:
      "Learn what a California Vehicle Code 21461(a) citation may involve, what to verify on the ticket, and why the exact sign, signal or device matters.",
    category: "Violation Codes",
    readingTime: "4 min read",
    heroSummary:
      "CVC 21461(a) generally concerns failure to obey certain official traffic signs, signals or devices. The exact citation facts still need careful review.",
    introduction: [
      "A ticket marked CVC 21461(a) can be confusing because the code number alone does not tell you which sign, signal or device the officer says was disobeyed.",
      "This guide is based on the official California Vehicle Code source and explains what to verify before deciding how to respond. It does not provide legal advice or predict the outcome of any case.",
    ],
    keyTakeaways: [
      "Vehicle Code section 21461(a) is about failing to obey certain official regulatory traffic signs, signals or devices.",
      "The citation should be reviewed for the exact sign, signal, location, date, time and officer or agency information.",
      "A code number alone is not enough to understand the case; the facts and court record matter.",
      "If the citation is unclear, the court docket or complete citation record may be needed before preparing a response.",
    ],
    sections: [
      {
        heading: "What CVC 21461(a) generally covers",
        body: [
          "The official California Vehicle Code section 21461(a) addresses a driver's failure to obey certain regulatory signs, signals or devices erected or maintained by lawful authority.",
          "In practical terms, a citation may involve an alleged failure to follow a posted traffic control instruction. The exact issue should come from the citation, officer statement or court record, not from the code number alone.",
          "Do not assume the same defense, evidence or records apply to every 21461(a) citation. The relevant facts depend on the sign, signal, location and circumstances alleged.",
        ],
      },
      {
        heading: "What to verify on the citation",
        body: [
          "Look for the full code section, any subsection, the description written by the officer, the location, the court, the agency, the date and time, and whether the citation identifies the sign or signal involved.",
          "If the code is handwritten, confirm whether it is 21461(a) or a different section. Similar-looking numbers can lead to the wrong research path.",
          "If the court portal lists a different charge than the ticket image, keep both records and resolve the discrepancy before drafting any response.",
        ],
      },
      {
        heading: "Evidence questions that may matter",
        body: [
          "Relevant evidence can depend on the alleged traffic device. A sign-related issue may involve sign placement, visibility, location context or photographs. A signal or device issue may involve different records.",
          "The point is not to claim a defect without support. The safer approach is to identify what needs confirmation: which sign or signal, where it was, whether it was visible, and what the officer says happened.",
          "If records are needed, the request should match the citation facts rather than ask for unrelated materials.",
        ],
      },
      {
        heading: "How this differs from other common ticket codes",
        body: [
          "A CVC 21461(a) citation is not the same thing as a generic speeding ticket or every red-light citation. Different code sections can carry different factual questions and records needs.",
          "If the citation description seems inconsistent with the code, treat that as a record-confirmation issue. The court docket, complete citation or officer declaration may clarify what is actually charged.",
          "Drivers should avoid copying language from a different violation type unless the citation facts actually support it.",
        ],
      },
      {
        heading: "Next steps before responding",
        body: [
          "Confirm the court deadline and instructions from the citation or court notice. Then review the exact code, location, alleged traffic device and any missing citation details.",
          "If the record is incomplete, consider whether a records request or court docket lookup is needed before choosing between payment, traffic school where available, written declaration or another response option.",
          "JusticeDrive can help organize a free initial citation review and identify missing fields or records questions, but it is not a law firm and does not provide legal advice.",
        ],
      },
    ],
    commonMistakes: [
      "Assuming the code alone explains which sign or signal was involved.",
      "Using a generic speeding-ticket or red-light argument without matching the citation facts.",
      "Ignoring a mismatch between the handwritten ticket and the court portal.",
      "Failing to photograph or preserve location context when visibility or signage is the practical issue.",
      "Missing the court deadline while researching the code.",
    ],
    faq: [
      {
        question: "What does CVC 21461(a) mean on a California ticket?",
        answer:
          "CVC 21461(a) generally relates to failing to obey certain official regulatory traffic signs, signals or devices. The exact allegation should be confirmed from the citation and court record.",
      },
      {
        question: "Is a CVC 21461(a) ticket the same as a red-light ticket?",
        answer:
          "Not necessarily. The code section and citation facts should be reviewed before treating it like another violation type.",
      },
      {
        question: "What evidence should I check for a 21461(a) citation?",
        answer:
          "Relevant evidence may include the citation description, location, sign or signal information, photographs, officer notes and any court or agency records tied to the alleged traffic device.",
      },
      {
        question: "Can JusticeDrive tell me if a 21461(a) ticket will be dismissed?",
        answer:
          "No. JusticeDrive can help organize citation facts and records questions, but it does not provide legal advice or guarantee any court outcome.",
      },
    ],
    sources: [officialSources.cvc21461, officialSources.courtsTraffic, officialSources.dmvViolations],
  }),
  "california-traffic-ticket-points": article({
    slug: "california-traffic-ticket-points",
    title: "California Traffic Ticket Points",
    description:
      "Understand how California DMV points may relate to traffic convictions, traffic school, insurance risk and response decisions.",
    category: "DMV Points and Traffic School",
    readingTime: "6 min read",
    heroSummary:
      "DMV points are one reason a traffic ticket can matter beyond the amount due. Whether points apply depends on the violation and how the case is resolved.",
    introduction: [
      "The dollar amount on a ticket is only one part of the decision. A California traffic conviction can also affect the DMV record, point count, and practical choices such as traffic school.",
      "This guide explains what points are, why the exact conviction matters, and how to review a ticket without assuming traffic school or insurance treatment is automatic.",
    ],
    keyTakeaways: [
      "A conviction may add a DMV point depending on the violation.",
      "Traffic school may mask a qualifying conviction from public insurance reporting, but eligibility is not guaranteed.",
      "The violation code and final court disposition matter more than a generic description.",
      "Drivers should review court and DMV materials before assuming point consequences.",
    ],
    sections: [
      {
        heading: "What California traffic ticket points mean",
        body: [
          "The California DMV tracks convictions and points for certain traffic violations. Points can matter because they may affect license status and how future violations are evaluated.",
          "The exact point impact depends on the violation, the conviction and DMV rules. Do not assume every citation has the same point consequence.",
          "The DMV's traffic violation materials explain that negligent operator actions can be based on point counts over time. That is why a driver with prior violations may view the same new citation differently than a driver with a clean recent record.",
          "The point question depends on the final court disposition. A citation is an allegation; the DMV consequence depends on what is ultimately reported as a conviction or other disposition.",
          "California DMV materials describe point counts over time rather than treating one ticket in isolation. That matters because a driver with no recent points, a driver with prior citations, and a commercial driver may face different practical risks from the same new ticket.",
          "For SEO readers, the key distinction is simple: the ticket starts a court process, while DMV points usually depend on the court-reported result. Before assuming the DMV impact, confirm how the case is charged and how it is resolved.",
        ],
      },
      {
        heading: "Why the violation code matters",
        body: [
          "The violation code is the starting point for understanding possible DMV consequences. A citation description like speeding is helpful, but the exact code should be confirmed.",
          "If the code is missing or hard to read, court records may be needed before you can reliably compare options.",
          "For example, a handwritten note saying speeding does not fully answer the point question. The exact Vehicle Code section and final court record provide the better basis for checking likely consequences.",
          "If the court portal lists a different charge than the citation face, preserve both records and resolve the discrepancy before making a decision.",
        ],
      },
      {
        heading: "Traffic school considerations",
        body: [
          "California Courts state that traffic school may be available for some eligible traffic tickets. The court decides whether it is offered and the driver must follow the court's instructions.",
          "Traffic school should not be described as erasing a ticket. It is a court-controlled option that may affect how an eligible conviction is reported, but the conviction and completion rules still matter.",
          "Commercial-driver situations can differ. Drivers with a commercial license or employment-related driving concerns should verify the rules that apply to their situation before relying on a general traffic-school explanation.",
          "A traffic-school option can change the practical decision, but it does not remove the need to understand the charge, deadline, fee instructions, and completion requirements.",
          "The court notice should explain whether traffic school is offered, how to request it, what must be paid, and when completion proof is due. If the notice is unclear, use the court portal or contact the court rather than relying on generic internet advice.",
          "If you complete traffic school outside the court's instructions or miss the completion deadline, the practical benefit may be lost. Keep proof of enrollment, completion, payment, and court confirmation.",
        ],
      },
      {
        heading: "Negligent-operator thresholds and why timing matters",
        body: [
          "DMV point consequences are often discussed together with negligent-operator thresholds. The timing of prior convictions can matter because the DMV evaluates point totals over defined periods.",
          "A driver should not assume that one ticket is harmless simply because it appears minor. If there are prior points, recent violations, commercial-driver concerns, or a pending DMV notice, the new citation may deserve closer review.",
          "JusticeDrive does not calculate an official DMV point total. The safe approach is to compare the citation and court record with the driver's DMV record and official DMV guidance.",
        ],
      },
      {
        heading: "Insurance and longer-term consequences",
        body: [
          "Insurance consequences vary by insurer, driving history and how the matter is resolved. It is not safe to promise a specific dollar impact from a citation.",
          "Drivers comparing options should consider both the court amount and possible longer-term consequences.",
          "JusticeDrive does not estimate insurance increases because insurer underwriting rules and driving history are not visible from the citation alone.",
          "A safer review asks which outcome would be reported, whether traffic school is offered, whether the driver is eligible, and whether any deadlines must be met to preserve that option.",
        ],
      },
      {
        heading: "When to review the citation before paying",
        body: [
          "Review can be useful when the citation code is unclear, the speed measurement is not explained, the location is incomplete or records may be needed to understand the charge.",
          "A careful review can help you decide whether to pay, ask about traffic school, request records or prepare a response.",
          "If the main concern is points, focus first on the exact charge and court disposition. If the main concern is contesting the allegation, focus on evidence, records, and procedure.",
          "Those are related but different questions. A ticket can be expensive even when the evidence is straightforward, and a ticket can have evidence gaps even when the point consequence appears familiar.",
        ],
      },
    ],
    commonMistakes: [
      "Assuming traffic school is automatically available.",
      "Ignoring the exact violation code when researching points.",
      "Treating online estimates as DMV confirmation.",
      "Focusing only on the fine and not the record consequences.",
      "Missing the court deadline while researching options.",
    ],
    faq: [
      {
        question: "Will every California speeding ticket add a point?",
        answer:
          "Not every situation is the same. A conviction may add a point depending on the violation and final disposition. Check the court notice and DMV materials.",
      },
      {
        question: "Does traffic school erase the ticket?",
        answer:
          "Traffic school does not mean the event never happened. For eligible cases, it may affect how the conviction is reported, but the court's rules and DMV treatment should be verified.",
      },
      {
        question: "Can points affect insurance?",
        answer:
          "Insurance consequences can vary by insurer and driving history. JusticeDrive does not estimate or guarantee insurance impact.",
      },
      {
        question: "What if my citation does not show a code?",
        answer:
          "The court docket or complete citation record may be needed to confirm the exact charge before relying on point or traffic-school assumptions.",
      },
    ],
    sources: [officialSources.dmvViolations, officialSources.trafficSchool, officialSources.courtsTraffic],
  }),
  "speeding-over-100-mph-california": article({
    slug: "speeding-over-100-mph-california",
    title: "Speeding Over 100 MPH California",
    description:
      "Learn why California speeding-over-100 allegations require careful review of the citation, speed evidence, court notice and possible DMV consequences.",
    category: "Speeding Tickets",
    readingTime: "6 min read",
    heroSummary:
      "A California allegation of driving over 100 mph is higher risk than an ordinary speeding ticket and should be reviewed carefully before choosing a response.",
    introduction: [
      "A citation alleging speed over 100 mph should be handled with more care than an ordinary speeding ticket. The allegation can involve different statutory consequences, court attention, and DMV concerns.",
      "This guide explains what to verify from the citation and official records before deciding how to respond. It does not provide legal advice and does not predict the outcome of any case.",
    ],
    keyTakeaways: [
      "Confirm whether the citation actually alleges a California over-100 mph violation.",
      "Review the alleged speed, posted speed, location, officer statement and speed-measurement basis.",
      "DMV and court consequences may be more serious than ordinary speeding, depending on the case.",
      "If consequences are significant, consider professional legal advice.",
    ],
    sections: [
      {
        heading: "Why over-100 mph cases deserve extra attention",
        body: [
          "California Vehicle Code section 22348 addresses certain high-speed violations. If your citation alleges more than 100 mph, the exact code and court notice should be reviewed closely.",
          "Do not rely on the handwritten description alone if the violation code is unclear or missing.",
          "The seriousness starts with the statutory section actually charged. Vehicle Code section 22348 includes language specific to driving greater than 100 miles per hour, and the court notice may identify consequences or appearance requirements that are not obvious from the citation face.",
          "Because the consequences can be significant, a driver should avoid treating the matter as a routine payment decision until the court record, alleged speed, code section, and deadline are confirmed.",
          "Section 22348 describes the over-100 allegation as an infraction and lists increasing statutory fine caps for repeat convictions. It also describes license-suspension consequences that may apply depending on prior qualifying convictions and the specific statutory subsection.",
          "That does not mean every citation produces the same result. It means the driver should read the charge carefully, check whether the allegation is actually under the over-100 subsection, and compare the court notice with the statute before making assumptions.",
        ],
      },
      {
        heading: "What to check on the citation",
        body: [
          "Look for the alleged speed, posted speed, location, officer notes, court name, violation code and any indication of radar, LiDAR, pacing or another measurement method.",
          "If the citation does not clearly show these facts, records may be needed before you can understand the strongest review areas.",
          "Confirm whether the citation specifically lists section 22348 or another speeding section. A citation can include a handwritten speed but still require the court charge entry to understand exactly what is filed.",
          "Also confirm whether the ticket gives a precise location. Speeding-over-100 allegations often depend heavily on the officer's observation and measurement context, so vague location text can make evidence review harder.",
          "If the citation image only shows one side, check whether the reverse side or later court notice contains appearance instructions, payment instructions, or deadline information. The California Courts traffic materials emphasize following the court handling the citation.",
          "If the violation code is blank, illegible, or inconsistent with the court portal, treat that as a record-confirmation problem before choosing a response path.",
        ],
      },
      {
        heading: "Speed-measurement evidence",
        body: [
          "The speed-measurement method can matter. Radar, LiDAR, pacing and visual estimate issues involve different records and different questions.",
          "A missing device record does not automatically prove anything. It tells you what may need to be requested or verified.",
          "If the record indicates radar or LiDAR, device identification, calibration or maintenance records, and officer training records may help verify the measurement basis. If the record indicates pacing, patrol vehicle speedometer or officer observation details may be more relevant.",
          "The driver's own memory can help identify questions, but the citation and official records should determine which records are actually requested.",
        ],
      },
      {
        heading: "DMV and traffic-school considerations",
        body: [
          "High-speed allegations may affect DMV and traffic-school analysis differently from ordinary tickets. Eligibility and consequences should be verified through court and DMV materials.",
          "Because consequences can be significant, drivers should be cautious about paying before reviewing the citation and court notice.",
          "Do not assume traffic school is available. California Courts materials explain that traffic school is limited to eligible matters and court instructions control the process.",
          "If the citation could affect license status, employment, commercial driving, or insurance risk, the driver should consider getting individualized advice before making a final choice.",
          "A driver should separate three questions: what the court may do with the citation, what the DMV may receive if there is a conviction, and what an insurer or employer may do with the record. Those questions are related, but they are not identical.",
          "JusticeDrive can help organize the visible citation facts and records questions. It does not decide eligibility, calculate an official DMV consequence, or replace advice from a qualified professional for high-risk situations.",
        ],
      },
      {
        heading: "Records that may help verify the allegation",
        body: [
          "Useful records can include the complete citation, court docket or charge entry, officer notes or declaration, device records if a device is indicated, and location-related materials such as photographs or diagrams.",
          "The purpose of these records is not to create a guaranteed defense. The purpose is to verify what the officer says happened, how speed was measured, and whether the official record supports the charged allegation.",
          "A focused records request should match the citation. Asking for every possible record can make the request less clear and may not produce useful information.",
        ],
      },
      {
        heading: "When to get additional help",
        body: [
          "If your license, employment, insurance or commercial driving status is at risk, software review may not be enough.",
          "JusticeDrive can organize the citation review and records questions, but it does not replace legal advice from a qualified attorney.",
          "This is especially true for a high-speed allegation where the court's available consequences, DMV treatment, and factual defenses may require individualized analysis.",
          "If you are close to a DMV point threshold or drive for work, get clarity before paying or submitting a court document.",
        ],
      },
    ],
    commonMistakes: [
      "Assuming the case is ordinary speeding without confirming the violation code.",
      "Ignoring whether the alleged speed was measured by radar, LiDAR, pacing or another method.",
      "Overlooking DMV consequences while focusing only on the fine.",
      "Missing court deadlines because the driver is waiting for more information.",
      "Using generic speed-trap language without confirming the applicable facts and records.",
    ],
    faq: [
      {
        question: "Is speeding over 100 mph more serious in California?",
        answer:
          "It can be. Drivers should review the specific violation code, court notice and DMV consequences and consider legal advice for high-risk cases.",
      },
      {
        question: "Can traffic school be used for an over-100 mph citation?",
        answer:
          "Eligibility depends on the court and violation. Do not assume traffic school is available without checking court instructions.",
      },
      {
        question: "What evidence should be reviewed?",
        answer:
          "The alleged speed, location, officer statement, measurement method and supporting records may all be relevant depending on the citation.",
      },
      {
        question: "Does JusticeDrive guarantee a defense?",
        answer:
          "No. JusticeDrive can identify issues and records to review, but it does not guarantee dismissal or provide legal advice.",
      },
    ],
    sources: [officialSources.cvc22348, officialSources.courtsTraffic, officialSources.dmvViolations],
  }),
  "radar-and-lidar-evidence-california": article({
    slug: "radar-and-lidar-evidence-california",
    title: "Radar and LiDAR Evidence California",
    description:
      "Learn how radar and LiDAR evidence may be reviewed in California speeding-ticket cases and what records may matter.",
    category: "Evidence and Records",
    readingTime: "5 min read",
    heroSummary:
      "Radar and LiDAR can be important in speeding-ticket review, but device issues should be tied to the citation facts and actual records, not assumptions.",
    introduction: [
      "Radar and LiDAR evidence can matter in a California speeding-ticket review, but only if the citation or official record shows that a device was actually part of the allegation.",
      "This guide explains what each record may help verify, when engineering-survey issues may be relevant, and why missing records should be treated as a verification issue rather than an automatic win.",
    ],
    keyTakeaways: [
      "Only focus on radar or LiDAR if the citation, officer statement or record indicates a device was used.",
      "Relevant records may include device logs, calibration records, officer training or other speed-measurement documentation.",
      "California speed-trap and survey questions are state-specific and fact-dependent.",
      "Device evidence is a review area, not a guaranteed dismissal path.",
    ],
    sections: [
      {
        heading: "Start with the measurement method",
        body: [
          "A speeding citation may be based on radar, LiDAR, pacing, visual estimate or another method. The first step is to identify what the citation or officer record actually says.",
          "Do not request device-specific records solely because the word speeding appears in a user narrative.",
          "Radar and LiDAR are different tools. Radar generally measures speed using radio waves; LiDAR generally uses laser pulses and target acquisition. The practical review question is not which technology sounds more technical, but which method the officer says was used.",
          "If the citation does not identify the method, a complete citation record, officer notes, or officer declaration may be needed before deciding whether device records are relevant.",
        ],
      },
      {
        heading: "Records that may matter",
        body: [
          "When radar or LiDAR is supported by the citation or records, relevant documents may include calibration logs, maintenance records, officer training records or the officer's declaration.",
          "The right request depends on what the citation actually alleges and which agency issued it.",
          "A clear records request should identify the citation, agency, date, location, and device type if known. If the device type is not known, the safer request is for the speed-measurement method and documents relied on by the officer.",
          "Records can help show whether a device was identified, whether maintenance or calibration information exists, and whether the officer describes how the reading was obtained.",
        ],
      },
      {
        heading: "What each record may help verify",
        body: [
          "Citation and officer notes may verify the alleged speed, posted speed, location, officer observations, and whether a device or pacing method is mentioned.",
          "Device identification records may verify the radar or LiDAR unit, serial number, agency ownership, or assignment information when those records are available.",
          "Calibration and maintenance records may help verify whether the agency has documentation about the device condition around the time of the citation.",
          "Training records may help verify whether the officer had documented training for the device or method described in the record.",
          "An engineering and traffic survey may be relevant in some California speed-trap contexts, but only when the violation, location, and enforcement facts make that issue applicable.",
          "Location diagrams or photographs may help clarify sight lines, signage, roadway layout, weather, traffic, or where the officer was positioned.",
          "An officer declaration may explain the factual basis for the allegation and can be especially important in a Trial by Written Declaration workflow.",
        ],
      },
      {
        heading: "Officer procedure and operation",
        body: [
          "Device evidence can involve questions about setup, distance, target identification, traffic conditions and whether the officer's statement explains the basis for the reading.",
          "These questions may support a records request or a written statement, but they should be grounded in facts.",
          "For example, a driver might ask whether the officer identified the target vehicle clearly, whether traffic conditions could affect the observation, or whether the declaration explains the measurement sequence.",
          "Those questions should be framed as issues to verify. They should not be framed as proven officer error unless the records support that conclusion.",
        ],
      },
      {
        heading: "Engineering survey and speed-trap issues",
        body: [
          "California has state-specific rules that may make engineering and traffic survey questions relevant in certain speed enforcement situations.",
          "Those issues should not be assumed from geocoding or a vague narrative alone. Location, road type, violation code and applicable records need confirmation.",
          "Vehicle Code section 40802 is the key official source for California speed-trap definitions. It does not mean every radar or LiDAR citation has a speed-trap issue.",
          "A credible review first confirms the exact location, road segment, code section, posted speed, and method of enforcement. Only then can survey or speed-trap questions be evaluated responsibly.",
        ],
      },
      {
        heading: "How to keep the review credible",
        body: [
          "A credible review separates device facts, citation facts and user observations. If the citation does not mention radar or LiDAR, the safer wording is that the speed-measurement method needs confirmation.",
          "JusticeDrive uses that separation so records recommendations do not become unsupported legal claims.",
          "The strongest record request is usually narrow enough to match the citation but broad enough to capture documents the agency relied on. For example, asking for the speed-measurement method, officer notes, and device records if a device was used is safer than asserting radar calibration is missing before seeing the record.",
          "If the records later show radar, LiDAR, pacing, or no stated method, the review can adapt without having made an unsupported claim up front.",
        ],
      },
    ],
    commonMistakes: [
      "Assuming radar or LiDAR was used without a citation clue.",
      "Treating missing calibration records as automatic dismissal.",
      "Ignoring officer procedure and location context.",
      "Using California speed-trap language when the state-specific facts are not confirmed.",
      "Requesting every possible record instead of the records tied to the citation gaps.",
    ],
    faq: [
      {
        question: "Should I always ask for radar records?",
        answer:
          "No. Radar-specific records make sense only when the citation or other case information indicates radar was used.",
      },
      {
        question: "Are LiDAR records different from radar records?",
        answer:
          "They can be. The device type may affect what logs, training or operation records are relevant.",
      },
      {
        question: "Does an engineering survey always matter?",
        answer:
          "No. Survey and speed-trap questions are California-specific and depend on the violation, location and enforcement facts.",
      },
      {
        question: "Can JusticeDrive request records?",
        answer:
          "JusticeDrive can help prepare records-request materials in supported workflows, but requests must be reviewed and submitted according to the applicable process.",
      },
    ],
    sources: [officialSources.cvc40802, officialSources.cvc22350, officialSources.courtsTraffic],
  }),
};

export const guideHubCards = [
  {
    title: "California Speeding Ticket Cost Calculator",
    description: "Estimate possible court cost once verified official data is available.",
    href: "/speeding-ticket-cost-calculator/",
    category: "Ticket Costs",
    status: "available",
  },
  ...guideIndex.map((guide) => ({
    title: guide.title,
    description: guide.description,
    href: guideHref(guide.slug),
    category: guide.category,
    status: "available",
  })),
  {
    title: "Red Light Camera Tickets in California",
    description: "Review red-light camera ticket evidence and records considerations.",
    href: "/violation/red-light/",
    category: "Red Light Tickets",
    status: "available",
  },
];
